import { resourceDetailsConfiguration } from "@planzone/dto";
import { events } from "@planzone/events";
import { Boundaries } from "@planzone/core";
import { createStateReducer } from "../../utils/createStateReducer";
import { setRequestStateToPending } from "../../utils/setRequestStateToPending";
import { setRequestStateToFailure } from "../../utils/setRequestStateToFailure";
import { setRequestStateToSuccess } from "../../utils/setRequestStateToSuccess";
import { State, Selector } from "./types";
import { createSelector } from "./selector";

const initialState: State = [];

function handleRetrieveResourceDetailsConfigurationRequest(
  state: State,
  planzone: Boundaries["retrieveResourceDetailsConfiguration"]["request"],
): State {
  const index = state.findIndex((req) => req.planzone.uuid === planzone.uuid);

  return index !== -1
    ? [
        ...state.slice(0, index),
        { ...state[index], request: setRequestStateToPending() },
        ...state.slice(index + 1),
      ]
    : [
        ...state,
        {
          planzone,
          request: setRequestStateToPending(),
          configuration: resourceDetailsConfiguration,
        },
      ];
}

function handleRetrieveResourceDetailsConfigurationSuccess(
  state: State,
  {
    planzone,
    configuration,
  }: Boundaries["retrieveResourceDetailsConfiguration"]["success"],
): State {
  const index = state.findIndex((req) => req.planzone.uuid === planzone.uuid);

  return index !== -1
    ? [
        ...state.slice(0, index),
        { ...state[index], request: setRequestStateToSuccess(), configuration },
        ...state.slice(index + 1),
      ]
    : [
        ...state,
        {
          planzone,
          request: setRequestStateToSuccess(),
          configuration,
        },
      ];
}

function handleRetrieveResourceDetailsConfigurationFailure(
  state: State,
  planzone: Boundaries["retrieveResourceDetailsConfiguration"]["failure"],
): State {
  const index = state.findIndex((req) => req.planzone.uuid === planzone.uuid);

  return index !== -1
    ? [
        ...state.slice(0, index),
        { ...state[index], request: setRequestStateToFailure() },
        ...state.slice(index + 1),
      ]
    : [
        ...state,
        {
          planzone,
          request: setRequestStateToFailure(),
          configuration: resourceDetailsConfiguration,
        },
      ];
}

function handleEditResourceDetailsConfigurationRequest(
  state: State,
  {
    planzone,
    nextConfiguration,
  }: Boundaries["editResourceDetailsConfiguration"]["request"],
): State {
  const index = state.findIndex((req) => req.planzone.uuid === planzone.uuid);

  return index !== -1
    ? [
        ...state.slice(0, index),
        { ...state[index], configuration: nextConfiguration },
        ...state.slice(index + 1),
      ]
    : [
        ...state,
        {
          planzone,
          request: setRequestStateToSuccess(),
          configuration: nextConfiguration,
        },
      ];
}

function handleEditResourceDetailsConfigurationSuccess(
  state: State,
  {
    planzone,
    configuration,
  }: Boundaries["editResourceDetailsConfiguration"]["success"],
): State {
  const index = state.findIndex((req) => req.planzone.uuid === planzone.uuid);

  return index !== -1
    ? [
        ...state.slice(0, index),
        { ...state[index], configuration },
        ...state.slice(index + 1),
      ]
    : [
        ...state,
        {
          planzone,
          request: setRequestStateToSuccess(),
          configuration,
        },
      ];
}

function handleEditResourceDetailsConfigurationFailure(
  state: State,
  {
    planzone,
    configuration,
  }: Boundaries["editResourceDetailsConfiguration"]["failure"],
): State {
  const index = state.findIndex((req) => req.planzone.uuid === planzone.uuid);

  return index !== -1
    ? [
        ...state.slice(0, index),
        { ...state[index], configuration },
        ...state.slice(index + 1),
      ]
    : [
        ...state,
        {
          planzone,
          request: setRequestStateToSuccess(),
          configuration,
        },
      ];
}

const reduceState = createStateReducer(initialState, {
  [events.retrieveResourceDetailsConfigurationRequested]:
    handleRetrieveResourceDetailsConfigurationRequest,
  [events.retrieveResourceDetailsConfigurationSucceeded]:
    handleRetrieveResourceDetailsConfigurationSuccess,
  [events.retrieveResourceDetailsConfigurationFailed]:
    handleRetrieveResourceDetailsConfigurationFailure,
  [events.editResourceDetailsConfigurationRequested]:
    handleEditResourceDetailsConfigurationRequest,
  [events.editResourceDetailsConfigurationSucceeded]:
    handleEditResourceDetailsConfigurationSuccess,
  [events.editResourceDetailsConfigurationFailed]:
    handleEditResourceDetailsConfigurationFailure,
});

export type { State, Selector };

export { reduceState, initialState, createSelector };
